/* eslint-disable no-unused-vars */
/* global G */
import { asyncPipeSpread, getFirstItem, isObj } from 'lib/util'
import sequenceComponentFind from 'lib/sequence/component/children/find'
import sequenceComponentState from 'lib/sequence/component/state'
import { get, set } from 'lib/sequence/component/state/value'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { disable, enable } from 'lib/sequence/component/state/disabled'
import listStatus from 'app/_shared/events/collection/listStatus'
import settings from '@tenant/settings'

const {
  unset: clearError,
} = sequenceComponentState('error')

const {
  unset: clearHelperText,
} = sequenceComponentState('helperText')

const {
  get: getConfiguration,
  set: setConfiguration,
} = sequenceComponentState('configuration')

const clear = component => clearError(component) && clearHelperText(component)

/**
 * Initializes the serial field according to the tenant's {@link settings}.
 *
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
export const initSerialField = async (components, ...args) => {
  const { serial } = components
  const { serialFieldConfig } = settings

  if (serialFieldConfig && isObj(serialFieldConfig)) {
    const configuration = getConfiguration(serial)
    const newConfiguration = { ...serialFieldConfig, ...configuration }
    setConfiguration(serial, newConfiguration)
  }

  return [components, ...args]
}

/**
 * Sets the `serialNumber`.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setSerial = module => async (components, ...args) => {
  // model
  const model = module[G.MODEL]
  const modelCache = model[G.CACHE]
  const { key } = modelCache || {}
  const { serial: modelSerial } = modelCache?.value || {}

  // component
  const { serial: serialField } = components

  const uiSerial = get(serialField)
  const newSerial = (key ? modelSerial : uiSerial) ?? null

  // setting the serial value from model if there is no one set
  set(serialField, newSerial)

  return [components, ...args]
}

/**
 * Sets the `numberCommission`.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
export const setCommissionNumber = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { numberCommission } = model[G.CACHE]?.value || {}
  // component
  const { numberCommission: numberCommissionField } = components

  if (settings.enableDevicesCommissionNumber) {
    clear(numberCommissionField)
    set(numberCommissionField, numberCommission || null)
    show(numberCommissionField)
  } else {
    hide(numberCommissionField)
  }

  return [components, ...args]
}

/**
 * Sets the `installedAt` field.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setInstalledAt = module => async (children, ...args) => {
  const { organisation } = args[0] || {}
  const { installedAt: installedAtField } = children

  organisation && set(installedAtField, [organisation[G.CACHE]])

  return [children, ...args]
}

/**
 * Sets the `name` field.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const setName = module => async (children, ...args) => {
  // model
  const model = module[G.MODEL]
  const { name } = model[G.CACHE]?.value || {}

  // component
  const { name: nameField } = children

  clear(nameField)
  if (name) {
    disable(nameField)
    set(nameField, name)
  } else {
    set(nameField, null)
    enable(nameField)
  }

  return [children, ...args]
}

/**
 * Sets the `status` field.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*): function(*, ...[*]): Promise<*>}
 */
const setStatus = module => component => async (children, ...args) => {
  const { status } = children

  const activeStatus = getFirstItem(listStatus(module, component, { detail: { key: 50 } }))

  set(status, activeStatus)

  return [children, ...args]
}
/**
 * ServiceItem action new
 *
 * Assigns the equipment found by searchSerial (if any) to the current model and the name of its
 * product as the Service Item's name.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  initSerialField,
  setSerial(module),
  setCommissionNumber(module),
  setInstalledAt(module),
  setName(module),
  setStatus(module)(component),
)(sequenceComponentFind(component), ...args)
